import { useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';

// Radetzky March - triumphant fanfare notes as audio context
const playRadetzkyFanfare = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Triumphant fanfare melody (simplified Radetzky-style)
    const notes = [
      { freq: 523.25, duration: 0.15 }, // C5
      { freq: 523.25, duration: 0.15 }, // C5
      { freq: 659.25, duration: 0.15 }, // E5
      { freq: 783.99, duration: 0.3 },  // G5
      { freq: 659.25, duration: 0.15 }, // E5
      { freq: 783.99, duration: 0.5 },  // G5 (hold)
      { freq: 880.00, duration: 0.15 }, // A5
      { freq: 783.99, duration: 0.15 }, // G5
      { freq: 659.25, duration: 0.15 }, // E5
      { freq: 783.99, duration: 0.6 },  // G5 (final hold)
    ];
    
    let startTime = audioContext.currentTime;
    
    notes.forEach((note) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.type = 'triangle'; // Brass-like sound
      oscillator.frequency.value = note.freq;
      
      // Envelope for each note
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(0.3, startTime + 0.02);
      gainNode.gain.linearRampToValueAtTime(0.2, startTime + note.duration * 0.5);
      gainNode.gain.linearRampToValueAtTime(0, startTime + note.duration);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + note.duration);
      
      startTime += note.duration;
    });
    
    // Add applause-like white noise
    setTimeout(() => {
      const bufferSize = audioContext.sampleRate * 2;
      const noiseBuffer = audioContext.createBuffer(1, bufferSize, audioContext.sampleRate);
      const output = noiseBuffer.getChannelData(0);
      
      for (let i = 0; i < bufferSize; i++) {
        output[i] = Math.random() * 2 - 1;
      }
      
      const whiteNoise = audioContext.createBufferSource();
      whiteNoise.buffer = noiseBuffer;
      
      const noiseGain = audioContext.createGain();
      const noiseFilter = audioContext.createBiquadFilter();
      
      noiseFilter.type = 'bandpass';
      noiseFilter.frequency.value = 1000;
      
      whiteNoise.connect(noiseFilter);
      noiseFilter.connect(noiseGain);
      noiseGain.connect(audioContext.destination);
      
      noiseGain.gain.setValueAtTime(0, audioContext.currentTime);
      noiseGain.gain.linearRampToValueAtTime(0.15, audioContext.currentTime + 0.1);
      noiseGain.gain.linearRampToValueAtTime(0.1, audioContext.currentTime + 1);
      noiseGain.gain.linearRampToValueAtTime(0, audioContext.currentTime + 2);
      
      whiteNoise.start();
      whiteNoise.stop(audioContext.currentTime + 2);
    }, 2000);
    
  } catch (e) {
    console.log('Audio not supported');
  }
};

// Sweet celebration sound for volunteers
const playSweetCelebration = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Gentle happy melody
    const notes = [
      { freq: 523.25, duration: 0.2 }, // C5
      { freq: 659.25, duration: 0.2 }, // E5
      { freq: 783.99, duration: 0.2 }, // G5
      { freq: 1046.50, duration: 0.4 }, // C6 (high, hold)
    ];
    
    let startTime = audioContext.currentTime;
    
    notes.forEach((note) => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.type = 'sine'; // Soft, sweet sound
      oscillator.frequency.value = note.freq;
      
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(0.2, startTime + 0.05);
      gainNode.gain.linearRampToValueAtTime(0, startTime + note.duration);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + note.duration);
      
      startTime += note.duration;
    });
  } catch (e) {
    console.log('Audio not supported');
  }
};

// Epic confetti celebration for the Queen
const fireRoyalConfetti = () => {
  const duration = 4000;
  const end = Date.now() + duration;
  
  // Crown colors: gold, purple, royal blue
  const colors = ['#FFD700', '#9333EA', '#6366F1', '#F59E0B', '#EC4899'];
  
  // Initial big burst
  confetti({
    particleCount: 100,
    spread: 70,
    origin: { y: 0.6 },
    colors: colors,
  });
  
  // Side cannons
  setTimeout(() => {
    confetti({
      particleCount: 50,
      angle: 60,
      spread: 55,
      origin: { x: 0 },
      colors: colors,
    });
    confetti({
      particleCount: 50,
      angle: 120,
      spread: 55,
      origin: { x: 1 },
      colors: colors,
    });
  }, 200);
  
  // Continuous rain
  const interval = setInterval(() => {
    if (Date.now() > end) {
      clearInterval(interval);
      return;
    }
    
    confetti({
      particleCount: 3,
      angle: 60,
      spread: 55,
      origin: { x: 0 },
      colors: colors,
    });
    confetti({
      particleCount: 3,
      angle: 120,
      spread: 55,
      origin: { x: 1 },
      colors: colors,
    });
  }, 50);
  
  // Star burst at the end
  setTimeout(() => {
    confetti({
      particleCount: 150,
      spread: 100,
      origin: { y: 0.5 },
      colors: ['#FFD700'],
      shapes: ['star'],
      scalar: 1.5,
    });
  }, 1500);
};

// Sweet heart confetti for volunteers
const fireHeartConfetti = () => {
  const duration = 3000;
  const end = Date.now() + duration;
  
  // Warm, loving colors
  const colors = ['#EC4899', '#F472B6', '#FB7185', '#FDA4AF', '#FECDD3', '#FF6B6B'];
  
  // Heart shape using custom path
  const heart = confetti.shapeFromPath({
    path: 'M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z'
  });
  
  // Initial burst of hearts
  confetti({
    particleCount: 60,
    spread: 80,
    origin: { y: 0.6 },
    colors: colors,
    shapes: [heart, 'circle'],
    scalar: 1.2,
  });
  
  // Gentle continuous hearts
  const interval = setInterval(() => {
    if (Date.now() > end) {
      clearInterval(interval);
      return;
    }
    
    confetti({
      particleCount: 2,
      angle: 60,
      spread: 45,
      origin: { x: 0 },
      colors: colors,
      shapes: [heart],
      scalar: 1,
    });
    confetti({
      particleCount: 2,
      angle: 120,
      spread: 45,
      origin: { x: 1 },
      colors: colors,
      shapes: [heart],
      scalar: 1,
    });
  }, 80);
  
  // Final burst
  setTimeout(() => {
    confetti({
      particleCount: 80,
      spread: 100,
      origin: { y: 0.5 },
      colors: colors,
      shapes: [heart, 'circle'],
      scalar: 1.3,
    });
  }, 1000);
};

export function triggerRoyalCelebration() {
  playRadetzkyFanfare();
  fireRoyalConfetti();
}

export function triggerVolunteerCelebration() {
  playSweetCelebration();
  fireHeartConfetti();
}

export default function RoyalCelebration({ trigger, onComplete }) {
  const hasTriggered = useRef(false);
  
  useEffect(() => {
    if (trigger && !hasTriggered.current) {
      hasTriggered.current = true;
      triggerRoyalCelebration();
      
      // Reset after animation
      setTimeout(() => {
        hasTriggered.current = false;
        if (onComplete) onComplete();
      }, 5000);
    }
  }, [trigger, onComplete]);
  
  return null; // This component just triggers effects
}
